/**
 * AppContentManager - Landing page content management
 * 
 * Thin orchestrator that delegates to tab-specific components.
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Save, RotateCcw, ArrowLeft, Home, Sparkles, Users, 
  MessageSquare, CreditCard, ExternalLink, Video, HelpCircle, Search, Image, LayoutGrid, Palette, Cookie, FileText, Gift, Tag, Heart, Mic
} from 'lucide-react';

import { BrandingTab } from '../components/cms/BrandingTab';
import { HeroTab } from '../components/cms/HeroTab';
import { FeaturesTab } from '../components/cms/FeaturesTab';
import { TestimonialsTab } from '../components/cms/TestimonialsTab';
import { PricingTab } from '../components/cms/PricingTab';
import { VideoTab } from '../components/cms/VideoTab';
import { FaqTab } from '../components/cms/FaqTab';
import { SeoTab } from '../components/cms/SeoTab';
import { CtaFooterTab } from '../components/cms/CtaFooterTab';
import { LayoutTab } from '../components/cms/LayoutTab';
import { CustomizationTab } from '../components/cms/CustomizationTab';
import { CookieTab } from '../components/cms/CookieTab';
import { MarketingPopupTab } from '../components/cms/MarketingPopupTab';
import { PrivacyPolicyTab } from '../components/cms/PrivacyPolicyTab';
import { ContactTab } from '../components/cms/ContactTab';
import { AboutTab } from '../components/cms/AboutTab';
import { OnboardingTab } from '../components/cms/OnboardingTab';
import { GalleryTab } from '../components/cms/GalleryTab';
import { PricingConfigTab } from '../components/cms/PricingConfigTab';
import WelfareResourcesTab from '../components/cms/WelfareResourcesTab';
import { VoiceFeaturesTab } from '../components/cms/VoiceFeaturesTab';
import { Hero2Tab } from '../components/cms/Hero2Tab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppContentManager() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const onboardingSaveRef = useRef(null);
  const pricingConfigSaveRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadContent(); }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/landing/content`);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/landing/content`, content, { headers });
      if (onboardingSaveRef.current) {
        await onboardingSaveRef.current();
      }
      if (pricingConfigSaveRef.current) {
        await pricingConfigSaveRef.current();
      }
      toast.success('Content saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await axios.post(`${API}/landing/content/reset`, {}, { headers });
      await loadContent();
      toast.success('Content reset to defaults');
      setShowResetDialog(false);
    } catch (error) {
      toast.error('Failed to reset content');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If the initial content fetch failed (cold start / transient gateway),
  // bail with a Retry rather than rendering tabs with content={null} — every
  // tab component dot-accesses `content.branding/.hero/...` and would crash.
  if (!content) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-4">
          <h2 className="text-xl font-bold text-gray-900">Couldn't load content</h2>
          <p className="text-sm text-gray-600">The landing-page content failed to load. This is usually a transient gateway issue — retry should work.</p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => navigate('/super-admin')} data-testid="cms-load-error-back">Back</Button>
            <Button onClick={loadContent} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cms-load-error-retry">Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          {/* Top row: Back + Title */}
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/super-admin')} className="shrink-0 px-2 sm:px-3">
              <ArrowLeft className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">App Content Manager</h1>
              <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Edit landing page content</p>
            </div>
            <NotificationBell />
          </div>
          {/* Action buttons row */}
          <div className="flex items-center gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => window.open('/', '_blank')} className="shrink-0">
              <ExternalLink className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Preview</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowResetDialog(true)} className="shrink-0">
              <RotateCcw className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Reset</span>
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 ml-auto shrink-0">
              <Save className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{saving ? 'Saving...' : 'Save Changes'}</span><span className="sm:hidden">{saving ? '...' : 'Save'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <Tabs defaultValue="branding" className="space-y-4 sm:space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-x-visible scrollbar-hide">
            <TabsList className="bg-white border inline-flex w-max sm:flex sm:flex-wrap sm:w-full sm:h-auto">
              <TabsTrigger value="branding" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Image className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Branding</span><span className="sm:hidden">Brand</span></TabsTrigger>
              <TabsTrigger value="layout" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><LayoutGrid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />Layout</TabsTrigger>
              <TabsTrigger value="hero" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Home className="w-3.5 h-3.5 sm:w-4 sm:h-4" />Hero</TabsTrigger>
              <TabsTrigger value="features" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Features</span><span className="sm:hidden">Feat</span></TabsTrigger>
              <TabsTrigger value="voice_features" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2" data-testid="tab-voice-features"><Mic className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Voice</span><span className="sm:hidden">Voice</span></TabsTrigger>
              <TabsTrigger value="hero2" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2" data-testid="tab-hero2"><Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Hero2</span><span className="sm:hidden">Hero2</span></TabsTrigger>
              <TabsTrigger value="customization" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Palette className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Make It Yours</span><span className="sm:hidden">Custom</span></TabsTrigger>
              <TabsTrigger value="video" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Video className="w-3.5 h-3.5 sm:w-4 sm:h-4" />Video</TabsTrigger>
              <TabsTrigger value="gallery" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Image className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Gallery</span><span className="sm:hidden">Gal</span></TabsTrigger>
              <TabsTrigger value="testimonials" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Testimonials</span><span className="sm:hidden">Review</span></TabsTrigger>
              <TabsTrigger value="pricing" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Pricing</span><span className="sm:hidden">Price</span></TabsTrigger>
              <TabsTrigger value="pricing_config" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2" data-testid="tab-pricing-config"><Tag className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Plans &amp; Billing</span><span className="sm:hidden">Plans</span></TabsTrigger>
              <TabsTrigger value="faq" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><HelpCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />FAQ</TabsTrigger>
              <TabsTrigger value="seo" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Search className="w-3.5 h-3.5 sm:w-4 sm:h-4" />SEO</TabsTrigger>
              <TabsTrigger value="cta" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">CTA & Footer</span><span className="sm:hidden">CTA</span></TabsTrigger>
              <TabsTrigger value="contact" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />Contact</TabsTrigger>
              <TabsTrigger value="about" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />About</TabsTrigger>
              <TabsTrigger value="cookies" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Cookie className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Cookies</span><span className="sm:hidden">Cook</span></TabsTrigger>
              <TabsTrigger value="marketing_popup" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Gift className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Marketing Popup</span><span className="sm:hidden">Popup</span></TabsTrigger>
              <TabsTrigger value="privacy" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Privacy Policy</span><span className="sm:hidden">Privacy</span></TabsTrigger>
              <TabsTrigger value="onboarding" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"><Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Onboarding</span><span className="sm:hidden">Onboard</span></TabsTrigger>
              <TabsTrigger value="welfare_resources" className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2" data-testid="tab-welfare-resources"><Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Welfare</span><span className="sm:hidden">Welf</span></TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="branding"><BrandingTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="layout"><LayoutTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="hero"><HeroTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="features"><FeaturesTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="voice_features"><VoiceFeaturesTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="hero2"><Hero2Tab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="customization"><CustomizationTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="video"><VideoTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="gallery"><GalleryTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="pricing"><PricingTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="pricing_config"><PricingConfigTab ref={pricingConfigSaveRef} /></TabsContent>
          <TabsContent value="faq"><FaqTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="seo"><SeoTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="cta"><CtaFooterTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="contact"><ContactTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="about"><AboutTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="cookies"><CookieTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="marketing_popup"><MarketingPopupTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="privacy"><PrivacyPolicyTab content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="onboarding"><OnboardingTab saveRef={onboardingSaveRef} /></TabsContent>
          <TabsContent value="welfare_resources"><WelfareResourcesTab /></TabsContent>
        </Tabs>
      </div>

      {/* Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Content</DialogTitle>
            <DialogDescription>Are you sure you want to reset all landing page content to defaults? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowResetDialog(false)} className="flex-1">Cancel</Button>
            <Button variant="destructive" onClick={handleReset} className="flex-1">Reset to Defaults</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
